import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { axiosInstance } from "@/lib/axios-instance";
import { cn, FILE_TYPE_MAPPING } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api-error";
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronsUpDown,
  ExternalLink,
  FileText,
  FileSpreadsheet,
  FileImage,
  File,
  Folder,
  FolderTree,
  Loader2,
  LogOut,
  Presentation,
  Search,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Link } from "lucide-react";
import { extractGoogleDriveFileId } from "@/lib/google-drive";

// Google Drive Icon Component
const GoogleDriveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
      fill="#0066da"
    />
    <path
      d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
      fill="#00ac47"
    />
    <path
      d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
      fill="#ea4335"
    />
    <path
      d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
      fill="#00832d"
    />
    <path
      d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
      fill="#2684fc"
    />
    <path
      d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
      fill="#ffba00"
    />
  </svg>
);

interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number | string;
  webViewLink?: string;
}

function ViewInDriveButton({ file }: { file: GoogleDriveFile }) {
  if (file.mimeType === "application/vnd.google-apps.folder") return null;
  const url =
    file.webViewLink || `https://drive.google.com/file/d/${file.id}/view`;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        window.open(url, "_blank", "noopener,noreferrer");
      }}
      title="Open in Google Drive"
      aria-label="Open in Google Drive"
      className="ml-auto flex-shrink-0 inline-flex items-center justify-center h-6 w-6 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </button>
  );
}

interface ImportedFile {
  _id: string;
  file_type: string;
  file_url?: string;
  file_name?: string;
}

interface GoogleDrivePickerProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  opportunityId: string;
  onImportComplete?: (files: ImportedFile[]) => void;
  onImportError?: (failedCount: number) => void;
  onSelectionConfirm?: (files: GoogleDriveFile[], accessToken: string) => void;
  selectedLabel?: string;
  mode?: "import" | "select";
}

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

// File type options from FILE_TYPE_MAPPING
const FILE_TYPE_OPTIONS = Object.entries(FILE_TYPE_MAPPING).map(
  ([label, value]) => ({
    label,
    value,
  })
);

declare global {
  interface Window {
    tokenClient?: unknown;
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient?: (config: unknown) => unknown;
        };
      };
    };
  }
}

export function GoogleDrivePicker({
  open,
  onClose,
  companyId,
  opportunityId,
  onImportComplete,
  onImportError,
  onSelectionConfirm,
  mode = "import",
}: GoogleDrivePickerProps) {
  const [breadcrumbs, setBreadcrumbs] = useState<
    { id: string; name: string }[]
  >([{ id: "root", name: "My Drive" }]);
  const [files, setFiles] = useState<GoogleDriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [fileTypes, setFileTypes] = useState<Record<string, string>>({});
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [folderFileMap, setFolderFileMap] = useState<Record<string, string[]>>({});
  // Store full file data for selected files so they persist across folder navigation
  const [selectedFilesData, setSelectedFilesData] = useState<Record<string, GoogleDriveFile>>({});
  // Track which folders were selected recursively (including subfolders)
  const [recursiveFolders, setRecursiveFolders] = useState<Set<string>>(new Set());
  // Loading state for recursive fetches (can be slow for deep hierarchies)
  const [recursiveLoadingFolders, setRecursiveLoadingFolders] = useState<Set<string>>(new Set());
  // Track which selected folders have subfolders inside them (for showing "+ subfolders" button)
  const [foldersWithSubfolders, setFoldersWithSubfolders] = useState<Set<string>>(new Set());
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const [googleDriveLink, setGoogleDriveLink] = useState("");
  const [isImportingLink, setIsImportingLink] = useState(false);
  const [linkAccessError, setLinkAccessError] = useState<{ message: string; fileId: string } | null>(null);
  const [fileTypePopoverOpen, setFileTypePopoverOpen] = useState<string | null>(null);
  // Paste link file type selection (like DocSend)
  const [pasteLinkFileType, setPasteLinkFileType] = useState<string>("");
  const [pasteLinkFileTypeOpen, setPasteLinkFileTypeOpen] = useState(false);

  const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (open && accessToken) {
      if (!isSearching) {
        fetchFiles(breadcrumbs[breadcrumbs.length - 1].id);
      }
    } else if (open && !accessToken) {
      setFiles([]);
      setSelectedFiles(new Set());
      setFileTypes({});
      setSearchQuery("");
      setIsSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, breadcrumbs, accessToken]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      } else if (searchQuery === "" && isSearching) {
        handleClearSearch();
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedFiles(new Set());
      setFileTypes({});
      setSelectedFilesData({});
      setSearchQuery("");
      setIsSearching(false);
      setSelectedFolders(new Set());
      setRecursiveFolders(new Set());
      setRecursiveLoadingFolders(new Set());
      setFolderFileMap({});
      setFoldersWithSubfolders(new Set());
    }
  }, [open]);

  const handleAuth = () => {
    if (!CLIENT_ID) {
      toast.error("Google Client ID is missing. Please contact support.");
      return;
    }

    const redirectUri = window.location.origin;
    const scope = "https://www.googleapis.com/auth/drive.readonly";
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}&prompt=select_account`;

    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      "Google Auth",
      `width=${width},height=${height},top=${top},left=${left}`
    );

    if (!popup) {
      toast.error("Pop-up blocked. Please allow pop-ups for this site.");
      return;
    }

    const pollTimer = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(pollTimer);
          return;
        }

        if (popup.location.href.includes("access_token")) {
          const hash = popup.location.hash.substring(1);
          const params = new URLSearchParams(hash);
          const token = params.get("access_token");

          if (token) {
            setAccessToken(token);
            toast.success("Connected to Google Drive");
            clearInterval(pollTimer);
            popup.close();
          }
        }
      } catch {
        // Cross-origin errors are expected until redirect back to origin
      }
    }, 500);
  };

  const handleDisconnect = () => {
    setAccessToken(null);
    setFiles([]);
    setBreadcrumbs([{ id: "root", name: "My Drive" }]);
    setSelectedFiles(new Set());
    setFileTypes({});
    setSearchQuery("");
    setIsSearching(false);
  };

  const fetchFiles = useCallback(
    async (folderId: string, query?: string) => {
      if (!accessToken) return;

      setLoading(true);
      setError(null);
      setFiles([]);
      // Don't reset selectedFiles and fileTypes here to preserve selections when navigating

      try {
        let url = `/google-drive/list-files?folder_url=${folderId}&google_access_token=${accessToken}`;
        if (query) {
          url += `&search_query=${encodeURIComponent(query)}`;
        }

        const response = await axiosInstance.get(url);

        if (response.data.status === "success") {
          setFiles(response.data.files);
        } else {
          setError(response.data.message || "Failed to fetch files");
        }
      } catch (err) {
        const error = err as {
          response?: { status?: number; data?: { message?: string } };
          message?: string;
        };
        if (error.response?.status === 401 || error.response?.status === 403) {
          setError("Session expired. Please reconnect.");
          setAccessToken(null);
        } else {
          setError(getErrorMessage(error, "Error connecting to Google Drive"));
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    fetchFiles("root", searchQuery.trim()); // Use "root" or any folderId, backend will ignore it if search_query is present
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    // This will trigger the useEffect because isSearching changed? No, useEffect dep doesn't include it.
    // We need to fetch explicitly.
    // Wait, the useEffect depends on [open, breadcrumbs, accessToken].
    // We should manually fetch here.
    fetchFiles(breadcrumbs[breadcrumbs.length - 1].id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleFolderClick = (folder: GoogleDriveFile) => {
    // If we were searching, clicking a folder should probably clear search and go into that folder
    if (isSearching) {
      setIsSearching(false);
      setSearchQuery("");
    }
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  const handleBackClick = () => {
    if (breadcrumbs.length > 1) {
      setBreadcrumbs(breadcrumbs.slice(0, -1));
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index < breadcrumbs.length - 1) {
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    }
  };

  const isGoogleDoc = (mimeType: string) => {
    return (
      mimeType.startsWith("application/vnd.google-apps.") &&
      mimeType !== "application/vnd.google-apps.folder"
    );
  };

  const isValidFileType = (mimeType: string) => {
    // Allow PDFs, docs, spreadsheets, presentations, images
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "image/jpeg",
      "image/png",
      "image/gif",
      "text/plain",
      "text/csv",
    ];
    // Also allow Google Docs types
    if (isGoogleDoc(mimeType)) return true;
    return validTypes.includes(mimeType) || mimeType.startsWith("image/");
  };

  const formatSize = (bytes?: number | string, mimeType?: string) => {
    if (mimeType && isGoogleDoc(mimeType)) {
      if (mimeType.includes("document")) return "Google Doc";
      if (mimeType.includes("spreadsheet")) return "Google Sheet";
      if (mimeType.includes("presentation")) return "Google Slide";
      return "Google File";
    }

    if (bytes === undefined || bytes === null || bytes === "") return "-";
    const size = Number(bytes);
    if (isNaN(size)) return "-";
    if (size === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    // PDF
    if (mimeType === "application/pdf") {
      return <FileText className="h-5 w-5 flex-shrink-0 text-red-500" />;
    }
    // Word documents
    if (
      mimeType.includes("word") ||
      mimeType.includes("document") ||
      mimeType === "application/vnd.google-apps.document"
    ) {
      return <FileText className="h-5 w-5 flex-shrink-0 text-blue-600" />;
    }
    // Excel/Spreadsheets
    if (
      mimeType.includes("excel") ||
      mimeType.includes("spreadsheet") ||
      mimeType === "application/vnd.google-apps.spreadsheet" ||
      mimeType === "text/csv"
    ) {
      return (
        <FileSpreadsheet className="h-5 w-5 flex-shrink-0 text-green-600" />
      );
    }
    // PowerPoint/Presentations
    if (
      mimeType.includes("powerpoint") ||
      mimeType.includes("presentation") ||
      mimeType === "application/vnd.google-apps.presentation"
    ) {
      return <Presentation className="h-5 w-5 flex-shrink-0 text-orange-500" />;
    }
    // Images
    if (mimeType.startsWith("image/")) {
      return <FileImage className="h-5 w-5 flex-shrink-0 text-purple-500" />;
    }
    // Default file icon
    return <File className="h-5 w-5 flex-shrink-0 text-gray-500" />;
  };

  const isFileSizeValid = (size?: number | string) => {
    if (!size) return true;
    const numSize = Number(size);
    return !isNaN(numSize) && numSize <= MAX_FILE_SIZE_BYTES;
  };

  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());

  // Fetch folder contents and add all valid files to selection
  // recursive=true fetches files from all subfolders as well
  const handleFolderSelect = async (folder: GoogleDriveFile, recursive: boolean = false) => {
    if (!accessToken) return;

    const folderId = folder.id;

    // Toggle - if folder is already selected, deselect it
    // Check if we should deselect (toggle off)
    // Don't deselect if we're upgrading from non-recursive to recursive
    const isUpgradingToRecursive = recursive && selectedFolders.has(folderId) && !recursiveFolders.has(folderId);
    const shouldDeselect = !isUpgradingToRecursive && (selectedFolders.has(folderId) || recursiveFolders.has(folderId));

    if (shouldDeselect) {
      const filesToRemove = folderFileMap[folderId] || [];
      setSelectedFiles((prev) => {
        const newSet = new Set(prev);
        filesToRemove.forEach((fileId) => newSet.delete(fileId));
        return newSet;
      });
      // Remove file data for deselected files
      setSelectedFilesData((prev) => {
        const newData = { ...prev };
        filesToRemove.forEach((fileId) => delete newData[fileId]);
        return newData;
      });
      setSelectedFolders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(folderId);
        return newSet;
      });
      setRecursiveFolders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(folderId);
        return newSet;
      });
      setFolderFileMap((prev) => {
        const newMap = { ...prev };
        delete newMap[folderId];
        return newMap;
      });
      setFoldersWithSubfolders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(folderId);
        return newSet;
      });
      toast.info(`Removed files from "${folder.name}"`);
      return;
    }

    // Set loading state based on whether it's recursive
    if (recursive) {
      setRecursiveLoadingFolders((prev) => new Set(prev).add(folderId));
      toast.loading(`Loading files from "${folder.name}" and subfolders...`, {
        id: `recursive-${folderId}`,
      });
    } else {
      setLoadingFolders((prev) => new Set(prev).add(folderId));
    }

    try {
      const recursiveParam = recursive ? '&recursive=true' : '';
      const url = `/google-drive/list-files?folder_url=${folderId}&google_access_token=${accessToken}${recursiveParam}`;
      const response = await axiosInstance.get(url);

      if (response.data.status === "success") {
        const folderFiles = response.data.files as GoogleDriveFile[];
        const validFiles = folderFiles.filter(
          (f) =>
            f.mimeType !== "application/vnd.google-apps.folder" &&
            isFileSizeValid(f.size) &&
            isValidFileType(f.mimeType)
        );

        // Check if this folder contains subfolders (only relevant for non-recursive fetch)
        if (!recursive) {
          const hasSubfoldersInside = folderFiles.some(
            (f) => f.mimeType === "application/vnd.google-apps.folder"
          );
          if (hasSubfoldersInside) {
            setFoldersWithSubfolders((prev) => new Set(prev).add(folderId));
          }
        }

        if (validFiles.length > 0) {
          const validFileIds = validFiles.map((f) => f.id);
          setSelectedFiles((prev) => {
            const newSet = new Set(prev);
            validFiles.forEach((f) => newSet.add(f.id));
            return newSet;
          });
          // Store file data for selected files
          setSelectedFilesData((prev) => {
            const newData = { ...prev };
            validFiles.forEach((f) => {
              newData[f.id] = f;
            });
            return newData;
          });
          // Add these files to the files list so they can be displayed/submitted
          setFiles((prev) => {
            const existingIds = new Set(prev.map((f) => f.id));
            const newFiles = validFiles.filter((f) => !existingIds.has(f.id));
            return [...prev, ...newFiles];
          });
          // Mark folder as selected and track its files
          if (recursive) {
            setRecursiveFolders((prev) => new Set(prev).add(folderId));
          } else {
            setSelectedFolders((prev) => new Set(prev).add(folderId));
          }
          setFolderFileMap((prev) => ({ ...prev, [folderId]: validFileIds }));

          const recursiveText = recursive ? ' and subfolders' : '';
          const skippedCount = folderFiles.filter(f => f.mimeType !== "application/vnd.google-apps.folder").length - validFiles.length;
          let successMessage = `Added ${validFiles.length} files from "${folder.name}"${recursiveText}`;
          if (skippedCount > 0) {
            successMessage += ` (${skippedCount} skipped - invalid type or size)`;
          }
          toast.success(successMessage);

          // Auto-scroll to show added files
          setTimeout(() => {
            const viewport = scrollAreaRef.current?.querySelector(
              "[data-radix-scroll-area-viewport]"
            );
            if (viewport) {
              viewport.scrollTo({
                top: viewport.scrollHeight,
                behavior: "smooth",
              });
            }
          }, 100);
        } else if (folderFiles.length > 0) {
          // No valid files, but folder has contents
          const hasSubfoldersInside = folderFiles.some(
            (f) => f.mimeType === "application/vnd.google-apps.folder"
          );

          if (hasSubfoldersInside && !recursive) {
            // Auto-trigger recursive fetch since folder has only subfolders
            toast.info(`"${folder.name}" contains subfolders, fetching all files...`);
            handleFolderSelect(folder, true);
            return;
          }

          // No subfolders and no valid files - show error
          const recursiveText = recursive ? ' or its subfolders' : '';
          toast.info(
            `No supported files in "${folder.name}"${recursiveText}. Only PDFs, docs, spreadsheets, presentations & images are allowed.`
          );
        } else {
          const recursiveText = recursive ? ' and its subfolders are' : ' is';
          toast.info(`"${folder.name}"${recursiveText} empty`);
        }
      }
    } catch (err) {
      console.error("Error fetching folder contents:", err);
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message || "Access denied or folder is restricted";
      toast.error(`Failed to load "${folder.name}": ${errorMessage}`);
    } finally {
      if (recursive) {
        toast.dismiss(`recursive-${folderId}`);
        setRecursiveLoadingFolders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(folderId);
          return newSet;
        });
      } else {
        setLoadingFolders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(folderId);
          return newSet;
        });
      }
    }
  };

  const handleToggleFile = (file: GoogleDriveFile) => {
    if (!isFileSizeValid(file.size) || !isValidFileType(file.mimeType)) return;

    const newSelected = new Set(selectedFiles);
    const newFileTypes = { ...fileTypes };
    const isDeselecting = newSelected.has(file.id);

    if (isDeselecting) {
      newSelected.delete(file.id);
      delete newFileTypes[file.id];
      // Remove from selectedFilesData
      setSelectedFilesData((prev) => {
        const newData = { ...prev };
        delete newData[file.id];
        return newData;
      });

      // Check if this file belonged to a folder and update folder selection
      let foundInFolderMap = false;
      for (const [folderId, fileIds] of Object.entries(folderFileMap)) {
        if (fileIds.includes(file.id)) {
          foundInFolderMap = true;
          // Check if any files from this folder are still selected
          const hasSelectedFiles = fileIds.some(
            (id) => id !== file.id && newSelected.has(id)
          );
          if (!hasSelectedFiles) {
            // Remove from both selectedFolders and recursiveFolders
            setSelectedFolders((prev) => {
              const newSet = new Set(prev);
              newSet.delete(folderId);
              return newSet;
            });
            setRecursiveFolders((prev) => {
              const newSet = new Set(prev);
              newSet.delete(folderId);
              return newSet;
            });
            // Clean up folder file map
            setFolderFileMap((prev) => {
              const newMap = { ...prev };
              delete newMap[folderId];
              return newMap;
            });
            setFoldersWithSubfolders((prev) => {
              const newSet = new Set(prev);
              newSet.delete(folderId);
              return newSet;
            });
          }
          break;
        }
      }

      // Also check current folder if we're inside one
      if (!foundInFolderMap && breadcrumbs.length > 1) {
        const currentFolderId = breadcrumbs[breadcrumbs.length - 1].id;
        if (selectedFolders.has(currentFolderId)) {
          // Deselect the folder since not all files are selected anymore
          setSelectedFolders((prev) => {
            const newSet = new Set(prev);
            newSet.delete(currentFolderId);
            return newSet;
          });
        }
      }
    } else {
      newSelected.add(file.id);
      // Store file data for later use
      setSelectedFilesData((prev) => ({
        ...prev,
        [file.id]: file,
      }));

      // Check if we're inside a folder and all valid files are now selected
      if (breadcrumbs.length > 1) {
        const currentFolderId = breadcrumbs[breadcrumbs.length - 1].id;
        const validFilesInFolder = files.filter(
          (f) =>
            f.mimeType !== "application/vnd.google-apps.folder" &&
            isFileSizeValid(f.size) &&
            isValidFileType(f.mimeType)
        );

        // Check if all valid files in current folder are now selected
        const allSelected = validFilesInFolder.every((f) =>
          newSelected.has(f.id)
        );

        if (allSelected && validFilesInFolder.length > 0) {
          // Mark folder as selected and update folderFileMap
          const validFileIds = validFilesInFolder.map((f) => f.id);
          setSelectedFolders((prev) => new Set(prev).add(currentFolderId));
          setFolderFileMap((prev) => ({
            ...prev,
            [currentFolderId]: validFileIds,
          }));
        }
      }
    }
    setSelectedFiles(newSelected);
    setFileTypes(newFileTypes);
  };

  const handleFileTypeChange = (fileId: string, type: string) => {
    setFileTypes((prev) => ({
      ...prev,
      [fileId]: type,
    }));
  };

  const handleSelectAll = () => {
    const allValidFiles = files.filter(
      (f) =>
        f.mimeType !== "application/vnd.google-apps.folder" &&
        isFileSizeValid(f.size) &&
        isValidFileType(f.mimeType)
    );

    // Check if all files in current view are selected
    const allInViewSelected = allValidFiles.every((f) =>
      selectedFiles.has(f.id)
    );

    if (allInViewSelected && allValidFiles.length > 0) {
      // Deselect all files in current view
      const newSelected = new Set(selectedFiles);
      allValidFiles.forEach((f) => newSelected.delete(f.id));
      setSelectedFiles(newSelected);

      // Remove file types for deselected files
      const newFileTypes = { ...fileTypes };
      allValidFiles.forEach((f) => delete newFileTypes[f.id]);
      setFileTypes(newFileTypes);

      // Remove file data for deselected files
      setSelectedFilesData((prev) => {
        const newData = { ...prev };
        allValidFiles.forEach((f) => delete newData[f.id]);
        return newData;
      });

      // If we're in a folder, deselect it
      if (breadcrumbs.length > 1) {
        const currentFolderId = breadcrumbs[breadcrumbs.length - 1].id;
        setSelectedFolders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(currentFolderId);
          return newSet;
        });
      }
    } else {
      // Select all files in current view
      const newSelected = new Set(selectedFiles);
      allValidFiles.forEach((f) => newSelected.add(f.id));
      setSelectedFiles(newSelected);

      // Store file data for selected files
      setSelectedFilesData((prev) => {
        const newData = { ...prev };
        allValidFiles.forEach((f) => {
          newData[f.id] = f;
        });
        return newData;
      });

      // If we're in a folder, mark it as selected
      if (breadcrumbs.length > 1 && allValidFiles.length > 0) {
        const currentFolderId = breadcrumbs[breadcrumbs.length - 1].id;
        const validFileIds = allValidFiles.map((f) => f.id);
        setSelectedFolders((prev) => new Set(prev).add(currentFolderId));
        setFolderFileMap((prev) => ({
          ...prev,
          [currentFolderId]: validFileIds,
        }));
      }
    }
  };

  const handleDeselectAll = () => {
    setSelectedFiles(new Set());
    setFileTypes({});
    setSelectedFilesData({});
    // Also clear all folder selections
    setSelectedFolders(new Set());
    setRecursiveFolders(new Set());
    setFolderFileMap({});
  };

  // Check if all selected files have types assigned
  const allFilesHaveTypes = Array.from(selectedFiles).every(
    (fileId) => fileTypes[fileId]
  );

  const handleImport = async () => {
    if (selectedFiles.size === 0 || (mode !== "select" && !allFilesHaveTypes))
      return;

    if (mode === "select") {
      // Get files from selectedFilesData (persists across folder navigation) or fall back to current files array
      const selectedFilesList = Array.from(selectedFiles)
        .map((id) => selectedFilesData[id] || files.find((f) => f.id === id))
        .filter(Boolean) as GoogleDriveFile[];

      // Attach the selected type to the file object for convenience
      const filesWithTypes = selectedFilesList.map((f) => ({
        ...f,
        selectedType: fileTypes[f.id],
      }));

      onSelectionConfirm?.(filesWithTypes, accessToken || "");
      onClose();
      return;
    }

    setImporting(true);
    const results: ImportedFile[] = [];
    let failedCount = 0;
    try {
      for (const fileId of selectedFiles) {
        // Get file from selectedFilesData or current files array
        const file = selectedFilesData[fileId] || files.find((f) => f.id === fileId);
        if (!file) continue;

        const selectedType = fileTypes[fileId];
        if (!selectedType) continue;

        try {
          const response = await axiosInstance.post("/google-drive/import-file", {
            company_id: companyId,
            opportunity_id: opportunityId,
            file_id: fileId,
            file_type: selectedType,
            google_access_token: accessToken,
          });

          if (response.data.status === "success") {
            results.push(response.data.file);
          }
        } catch (fileErr) {
          console.error(`Failed to import file ${fileId}:`, fileErr);
          failedCount++;
        }
      }

      if (results.length > 0) {
        onImportComplete?.(results);
      }
      if (failedCount > 0) {
        onImportError?.(failedCount);
        toast.error(`Failed to import ${failedCount} file${failedCount !== 1 ? "s" : ""}`);
      }
      if (results.length > 0) {
        toast.success(
          `Successfully imported ${results.length} file${results.length !== 1 ? "s" : ""}`
        );
        onClose();
      }
    } catch (err: unknown) {
      const error = err as Error;
      console.error("Import error:", err);
      toast.error(getErrorMessage(error, "Failed to import some files"));
    } finally {
      setImporting(false);
    }
  };

  const handleGoogleDriveLinkSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Clear any previous access error
    setLinkAccessError(null);

    if (!googleDriveLink.trim()) {
      toast.error("Please enter a valid Google Drive link.");
      return;
    }

    const fileId = extractGoogleDriveFileId(googleDriveLink);
    if (!fileId) {
      toast.error("Could not extract File ID from the provided Google Drive link.");
      return;
    }

    setIsImportingLink(true);

    try {
      // In select mode, get file info and add to selection
      if (mode === "select") {
        const response = await axiosInstance.get("/google-drive/file-info", {
          params: { file_id: fileId },
        });

        if (response.data.status === "success" && response.data.file) {
          const fileInfo = response.data.file as GoogleDriveFile;
          toast.success(`File "${fileInfo.name}" added from Google Drive`);
          setGoogleDriveLink("");
          // Pass the file to selection confirm - no access token needed since we use service account
          onSelectionConfirm?.([fileInfo], "service_account");
          onClose();
        } else {
          throw new Error(response.data.message || "Failed to get file info");
        }
        return;
      }

      // In import mode, import directly (requires companyId)
      if (!companyId) {
        toast.error("Cannot import file: company ID is missing.");
        return;
      }

      // Validate file type is selected
      if (!pasteLinkFileType) {
        toast.error("Please select a document type");
        return;
      }

      const response = await axiosInstance.post("/google-drive/import-file", {
        company_id: companyId,
        opportunity_id: opportunityId,
        file_id: fileId,
        file_type: pasteLinkFileType,
      });

      if (response.data.status === "success") {
        toast.success("File imported successfully from Google Drive");
        setGoogleDriveLink("");

        if (response.data.file) {
          // Transform file name to use file type label (like DocSend)
          const fileExtension = response.data.file.file_name?.split('.').pop() || 'pdf';
          const selectedFileTypeLabel = FILE_TYPE_OPTIONS.find(
            (opt) => opt.value === pasteLinkFileType
          )?.label || pasteLinkFileType;
          const displayName = `${selectedFileTypeLabel}.${fileExtension}`;

          onImportComplete?.([{
            ...response.data.file,
            file_name: displayName,
          }]);
        } else {
          // Fallback in case the endpoint doesn't return the exact file object but succeeds
          // The parent components usually refetch or we can just pass a dummy to trigger refresh
          onImportComplete?.([{ _id: "new", file_type: pasteLinkFileType }]);
        }
        // Reset file type state
        setPasteLinkFileType("");
        onClose();
      } else {
        throw new Error(response.data.message || "Import failed");
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string }; status?: number }; message?: string };
      console.error("Error with Drive link:", error);

      const errorMessage = error.response?.data?.message || error.message || "";
      const isAccessError = errorMessage.includes("not accessible") ||
                           errorMessage.includes("not found") ||
                           error.response?.status === 404 ||
                           error.response?.status === 403;

      if (isAccessError && fileId) {
        // Show inline error with "Request Access" button inside the modal
        setLinkAccessError({
          message: "This file is private or not accessible. Request access from the file owner.",
          fileId: fileId,
        });
      } else {
        toast.error(
          errorMessage || "Failed to process file."
        );
      }
    } finally {
      setIsImportingLink(false);
    }
  };

  // Get all selected files from persisted data or current view
  const selectedFilesList = Array.from(selectedFiles)
    .map((id) => selectedFilesData[id] || files.find((f) => f.id === id))
    .filter(Boolean) as GoogleDriveFile[];
  const selectedTotalSize = selectedFilesList.reduce((acc, file) => {
    if (isGoogleDoc(file.mimeType)) return acc;
    return acc + (Number(file.size) || 0);
  }, 0);
  const selectedGoogleDocsCount = selectedFilesList.filter((f) =>
    isGoogleDoc(f.mimeType)
  ).length;

  const validFilesInView = files.filter(
    (f) =>
      f.mimeType !== "application/vnd.google-apps.folder" &&
      isFileSizeValid(f.size) &&
      isValidFileType(f.mimeType)
  );
  const validFilesCount = validFilesInView.length;
  // Check if all valid files in current view are selected
  const allInViewSelected =
    validFilesCount > 0 &&
    validFilesInView.every((f) => selectedFiles.has(f.id));
  // Check if some (but not all) files in current view are selected
  const selectedInViewCount = validFilesInView.filter((f) =>
    selectedFiles.has(f.id)
  ).length;
  const allSelected = allInViewSelected;
  const someSelected =
    selectedInViewCount > 0 && selectedInViewCount < validFilesCount;

  const filesWithoutTypes = Array.from(selectedFiles).filter(
    (fileId) => !fileTypes[fileId]
  ).length;
  const canImport =
    selectedFiles.size > 0 &&
    (mode === "select" || allFilesHaveTypes) &&
    !importing;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GoogleDriveIcon className="h-5 w-5" />
            {mode === "select"
              ? "Select from Google Drive"
              : "Import from Google Drive"}
          </DialogTitle>
          <DialogDescription>
            {accessToken
              ? "Select files and assign document types to import them."
              : "Connect your Google Drive to import files."}
          </DialogDescription>
        </DialogHeader>

        {!accessToken ? (
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col items-center py-8 gap-6 w-full max-w-lg mx-auto min-h-full justify-center">
              <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-full">
              <GoogleDriveIcon className="h-12 w-12" />
            </div>

            <div className="w-full space-y-4">
              <div className="space-y-2">
                <Label htmlFor="drive-link" className="text-sm font-medium text-gray-700">
                  Paste Google Drive Link
                </Label>
                <form onSubmit={handleGoogleDriveLinkSubmit} className="flex gap-2">
                  <Input
                    id="drive-link"
                    placeholder="https://docs.google.com/document/d/..."
                    value={googleDriveLink}
                    onChange={(e) => {
                      setGoogleDriveLink(e.target.value);
                      // Clear error when user starts typing a new link
                      if (linkAccessError) setLinkAccessError(null);
                    }}
                    disabled={isImportingLink}
                    className={`bg-white ${linkAccessError ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                  />
                  <Button
                    type="submit"
                    disabled={!googleDriveLink.trim() || isImportingLink || (mode === "import" && !pasteLinkFileType)}
                    className="shrink-0"
                  >
                    {isImportingLink ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Link className="h-4 w-4" />
                    )}
                    <span className="sr-only">Import Link</span>
                  </Button>
                </form>
                {/* Document Type Selection - Required for import mode */}
                {mode === "import" && (
                  <div className="space-y-1.5 mt-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Document Type <span className="text-destructive">*</span>
                    </Label>
                    <Popover open={pasteLinkFileTypeOpen} onOpenChange={setPasteLinkFileTypeOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={pasteLinkFileTypeOpen}
                          className="w-full justify-between bg-white"
                          disabled={isImportingLink}
                        >
                          {pasteLinkFileType
                            ? FILE_TYPE_OPTIONS.find((opt) => opt.value === pasteLinkFileType)?.label
                            : "Select document type..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                        <Command>
                          <CommandInput placeholder="Search document type..." />
                          <CommandList>
                            <CommandEmpty>No document type found.</CommandEmpty>
                            <CommandGroup>
                              {FILE_TYPE_OPTIONS.map((option) => (
                                <CommandItem
                                  key={option.value}
                                  value={option.label}
                                  onSelect={() => {
                                    setPasteLinkFileType(option.value);
                                    setPasteLinkFileTypeOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      pasteLinkFileType === option.value ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {option.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                {linkAccessError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-red-700">{linkAccessError.message}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2 text-red-600 border-red-300 hover:bg-red-100 hover:text-red-700"
                        onClick={() => {
                          window.open(`https://drive.google.com/file/d/${linkAccessError.fileId}/view`, "_blank");
                        }}
                      >
                        Request Access
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink-0 mx-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">OR</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <div className="text-center space-y-3 pt-2">
                <p className="text-gray-600 text-sm">
                  Connect your Google Drive account to browse and select files directly.
                </p>
                <Button onClick={handleAuth} variant="outline" className="w-full gap-2 h-12">
                  <GoogleDriveIcon className="h-5 w-5" />
                  Connect Google Drive
                </Button>
                {!CLIENT_ID && (
                  <p className="text-xs text-red-500 mt-2">
                    Configuration Error: Missing Google Client ID.
                  </p>
                )}
              </div>
            </div>
          </div>
          </div>
        ) : (
          <>
            {/* Search and Navigation Bar */}
            <div className="flex flex-col gap-2 py-2 px-3 border rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search files and folders..."
                    className="pl-9 pr-8 bg-white"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={handleSearch}
                  disabled={!searchQuery.trim() || loading}
                >
                  Search
                </Button>
              </div>

              <div className="flex items-center gap-2 pt-1 border-t mt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackClick}
                  disabled={breadcrumbs.length <= 1 || loading || isSearching}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 flex items-center gap-1 text-sm font-medium truncate">
                  {isSearching ? (
                    <span className="flex items-center gap-1 text-gray-900">
                      <Search className="h-3 w-3" />
                      Search Results: "{searchQuery}"
                    </span>
                  ) : (
                    <div className="flex-1 flex items-center gap-1 truncate scrollbar-hide overflow-x-auto whitespace-nowrap text-sm font-medium">
                      {breadcrumbs.map((b, i) => {
                        const isLast = i === breadcrumbs.length - 1;
                        return (
                          <span
                            key={b.id || i}
                            className="flex items-center gap-1 min-w-0 flex-shrink-0"
                          >
                            {i > 0 && <span className="text-gray-400">/</span>}
                            <button
                              onClick={() => handleBreadcrumbClick(i)}
                              disabled={isLast || loading}
                              className={`truncate max-w-[150px] ${isLast
                                ? "text-gray-900 cursor-default font-semibold"
                                : "text-gray-500 hover:text-blue-600 hover:underline cursor-pointer"
                                }`}
                            >
                              {b.name}
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDisconnect}
                  title="Disconnect"
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* File List */}
            <div className="flex-1 overflow-hidden border rounded-lg relative flex flex-col min-h-0">
              {loading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}

              {/* Desktop Headers */}
              <div
                className={`hidden sm:grid ${mode === "select"
                  ? "grid-cols-[auto_1fr_80px]"
                  : "grid-cols-[auto_1fr_200px_80px]"
                  } gap-3 p-3 border-b bg-gray-50 text-xs font-semibold text-gray-500 items-center`}
              >
                <Checkbox
                  id="header-checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) {
                      (el as unknown as HTMLInputElement).indeterminate =
                        someSelected;
                    }
                  }}
                  onCheckedChange={handleSelectAll}
                  disabled={validFilesCount === 0 || importing}
                />
                <div className="flex items-center gap-2 min-w-0">
                  <label
                    htmlFor="header-checkbox"
                    className="cursor-pointer select-none hover:text-gray-900 truncate"
                  >
                    {selectedFiles.size > 0
                      ? `${selectedFiles.size} selected`
                      : "Name"}
                  </label>
                  {selectedFiles.size > 0 && (
                    <button
                      onClick={handleDeselectAll}
                      className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline font-normal flex-shrink-0"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                {mode !== "select" && <div>Document Type</div>}
                <div className="text-right">Size</div>
              </div>

              <ScrollArea className="flex-1" ref={scrollAreaRef}>
                <div className="divide-y">
                  {files.length === 0 && !loading && (
                    <div className="text-center py-12 text-gray-500">
                      {isSearching ? (
                        <>
                          <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p>No files found matching "{searchQuery}"</p>
                          <Button
                            variant="link"
                            onClick={handleClearSearch}
                            className="mt-2 text-blue-600"
                          >
                            Clear search
                          </Button>
                        </>
                      ) : (
                        <>
                          <Folder className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                          <p>This folder is empty</p>
                        </>
                      )}
                    </div>
                  )}
                  {files.map((file) => {
                    const isFolder =
                      file.mimeType === "application/vnd.google-apps.folder";
                    const isGDoc = isGoogleDoc(file.mimeType);
                    const isSizeValid = isFileSizeValid(file.size);
                    const isTypeValid = isValidFileType(file.mimeType);
                    const isValid = isFolder || (isSizeValid && isTypeValid);
                    const isSelected = selectedFiles.has(file.id);
                    const hasType = !!fileTypes[file.id];

                    return (
                      <div
                        key={file.id}
                        data-state={isSelected ? "selected" : "unchecked"}
                        onClick={() =>
                          !isFolder &&
                          isValid &&
                          !importing &&
                          handleToggleFile(file)
                        }
                        className={`
                                                    block sm:grid ${mode === "select"
                            ? "sm:grid-cols-[auto_1fr_80px]"
                            : "sm:grid-cols-[auto_1fr_200px_80px]"
                          } 
                                                    items-center gap-3 p-3 transition-colors
                                                    ${!isFolder && isValid
                            ? "cursor-pointer"
                            : ""
                          }
                                                    ${isSelected
                            ? "bg-blue-50 hover:bg-blue-100"
                            : "hover:bg-gray-50"
                          }
                                                    ${!isValid && !isFolder
                            ? "opacity-50 bg-red-50/50 cursor-not-allowed"
                            : ""
                          }
                                                `}
                      >
                        {/* Mobile Layout */}
                        <div className="sm:hidden overflow-hidden">
                          {/* Row 1: Checkbox + Icon + Filename */}
                          <div className="flex items-center gap-3 overflow-hidden">
                            {/* Checkbox - for both files and folders */}
                            {isFolder ? (
                              <div
                                className="flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {(loadingFolders.has(file.id) || recursiveLoadingFolders.has(file.id)) ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                ) : (
                                  <Checkbox
                                    checked={selectedFolders.has(file.id) || recursiveFolders.has(file.id)}
                                    onCheckedChange={() => handleFolderSelect(file, false)}
                                    disabled={importing}
                                    className={recursiveFolders.has(file.id) ? "data-[state=checked]:bg-blue-600" : ""}
                                  />
                                )}
                              </div>
                            ) : (
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleToggleFile(file)}
                                onClick={(e) => e.stopPropagation()}
                                disabled={!isValid || importing}
                                className="flex-shrink-0"
                              />
                            )}

                            {/* Icon + File Name */}
                            <div
                              className={`flex items-center gap-2 flex-1 min-w-0 ${isFolder ? "cursor-pointer" : ""
                                }`}
                              onClick={(e) => {
                                if (isFolder) {
                                  e.stopPropagation();
                                  handleFolderClick(file);
                                }
                              }}
                            >
                              {isFolder ? (
                                <Folder className="h-5 w-5 flex-shrink-0 text-yellow-500" />
                              ) : (
                                getFileIcon(file.mimeType)
                              )}
                              <p
                                className={`w-0 flex-grow truncate text-sm ${isFolder
                                  ? "text-blue-600 hover:underline font-medium"
                                  : ""
                                  } ${isSelected ? "font-medium text-blue-900" : ""
                                  }`}
                                title={file.name}
                              >
                                {file.name}
                              </p>
                              {/* Badge for recursive folder selection - show actual selected count */}
                              {isFolder && recursiveFolders.has(file.id) && folderFileMap[file.id] && (
                                <span className="ml-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                                  {folderFileMap[file.id].filter(id => selectedFiles.has(id)).length} files (incl. subfolders)
                                </span>
                              )}
                              {/* Show file count for non-recursive selection - show actual selected count */}
                              {isFolder && selectedFolders.has(file.id) && !recursiveFolders.has(file.id) && folderFileMap[file.id] && (
                                <span className="ml-1 text-xs text-gray-500 whitespace-nowrap">
                                  {folderFileMap[file.id].filter(id => selectedFiles.has(id)).length} files
                                </span>
                              )}
                              {/* Show "+ subfolders" button when folder has both files AND subfolders */}
                              {isFolder && selectedFolders.has(file.id) && !recursiveFolders.has(file.id) && foldersWithSubfolders.has(file.id) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFolderSelect(file, true);
                                  }}
                                  disabled={importing || recursiveLoadingFolders.has(file.id)}
                                  className="ml-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap flex items-center gap-1"
                                >
                                  <FolderTree className="h-3 w-3" />
                                  + subfolders
                                </button>
                              )}
                              <ViewInDriveButton file={file} />
                            </div>
                          </div>

                          {/* Row 2: Size (below filename, with indentation) */}
                          {!isFolder && (
                            <div className="mt-1 pl-7 flex items-center gap-2">
                              <span className="text-xs text-gray-400">
                                Size:
                              </span>
                              <div className="flex items-center gap-1">
                                {!isSizeValid && (
                                  <span className="text-red-500 text-[10px] flex items-center gap-0.5">
                                    <AlertCircle className="h-3 w-3" />
                                    &gt;25MB
                                  </span>
                                )}
                                {!isTypeValid && isSizeValid && (
                                  <span className="text-orange-500 text-[10px] flex items-center gap-0.5">
                                    <AlertCircle className="h-3 w-3" />
                                    Invalid
                                  </span>
                                )}
                                <span
                                  className={`text-xs ${!isValid
                                    ? "text-red-500"
                                    : isGDoc
                                      ? "text-blue-600 font-medium"
                                      : "text-gray-600"
                                    }`}
                                >
                                  {formatSize(file.size, file.mimeType)}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Row 3: Document Type dropdown (if import mode and selected) */}
                          {mode !== "select" && !isFolder && isSelected && (
                            <div className="mt-2 pl-7" onClick={(e) => e.stopPropagation()}>
                              <Popover
                                modal={false}
                                open={fileTypePopoverOpen === `mobile-${file.id}`}
                                onOpenChange={(open) => setFileTypePopoverOpen(open ? `mobile-${file.id}` : null)}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={fileTypePopoverOpen === `mobile-${file.id}`}
                                    disabled={importing}
                                    className={cn(
                                      "h-8 w-full justify-between text-xs",
                                      !hasType && "border-orange-300 bg-orange-50"
                                    )}
                                  >
                                    {fileTypes[file.id]
                                      ? FILE_TYPE_OPTIONS.find((opt) => opt.value === fileTypes[file.id])?.label
                                      : "Select type..."}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[200px] p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
                                  <Command>
                                    <CommandInput placeholder="Search type..." />
                                    <CommandList>
                                      <CommandEmpty>No type found.</CommandEmpty>
                                      <CommandGroup>
                                        {FILE_TYPE_OPTIONS.map((option) => (
                                          <CommandItem
                                            key={option.value}
                                            value={option.label}
                                            onSelect={() => {
                                              handleFileTypeChange(file.id, option.value);
                                              setFileTypePopoverOpen(null);
                                            }}
                                            className="text-xs"
                                          >
                                            <Check className={cn("mr-2 h-4 w-4", fileTypes[file.id] === option.value ? "opacity-100" : "opacity-0")} />
                                            {option.label}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden sm:contents">
                          {/* Checkbox - for both files and folders */}
                          {isFolder ? (
                            <div
                              className="flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(loadingFolders.has(file.id) || recursiveLoadingFolders.has(file.id)) ? (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                              ) : (
                                <Checkbox
                                  checked={selectedFolders.has(file.id) || recursiveFolders.has(file.id)}
                                  onCheckedChange={() => handleFolderSelect(file, false)}
                                  disabled={importing}
                                  className={recursiveFolders.has(file.id) ? "data-[state=checked]:bg-blue-600" : ""}
                                />
                              )}
                            </div>
                          ) : (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleToggleFile(file)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={!isValid || importing}
                              className="flex-shrink-0"
                            />
                          )}

                          {/* File Name */}
                          <div
                            className={`flex items-center gap-2 min-w-0 overflow-hidden ${isFolder ? "cursor-pointer" : ""
                              }`}
                            onClick={(e) => {
                              if (isFolder) {
                                e.stopPropagation();
                                handleFolderClick(file);
                              }
                            }}
                          >
                            {isFolder ? (
                              <Folder className="h-5 w-5 flex-shrink-0 text-yellow-500" />
                            ) : (
                              getFileIcon(file.mimeType)
                            )}
                            <span
                              className={`truncate text-sm ${isFolder
                                ? "text-blue-600 hover:underline font-medium"
                                : ""
                                } ${isSelected ? "font-medium text-blue-900" : ""
                                }`}
                              title={file.name}
                            >
                              {file.name}
                            </span>
                            {/* Badge for recursive folder selection - show actual selected count */}
                            {isFolder && recursiveFolders.has(file.id) && folderFileMap[file.id] && (
                              <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                                {folderFileMap[file.id].filter(id => selectedFiles.has(id)).length} files (incl. subfolders)
                              </span>
                            )}
                            {/* Show file count for non-recursive selection - show actual selected count */}
                            {isFolder && selectedFolders.has(file.id) && !recursiveFolders.has(file.id) && folderFileMap[file.id] && (
                              <span className="ml-2 text-xs text-gray-500 whitespace-nowrap">
                                {folderFileMap[file.id].filter(id => selectedFiles.has(id)).length} files
                              </span>
                            )}
                            {/* Show "+ subfolders" button when folder has both files AND subfolders */}
                            {isFolder && selectedFolders.has(file.id) && !recursiveFolders.has(file.id) && foldersWithSubfolders.has(file.id) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFolderSelect(file, true);
                                }}
                                disabled={importing || recursiveLoadingFolders.has(file.id)}
                                className="ml-2 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap flex items-center gap-1"
                              >
                                <FolderTree className="h-3 w-3" />
                                + subfolders
                              </button>
                            )}
                            <ViewInDriveButton file={file} />
                          </div>

                          {/* Document Type Dropdown (Desktop) */}
                          {mode !== "select" && (
                            <div
                              className="min-w-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {!isFolder && isSelected ? (
                                <Popover
                                  modal={false}
                                  open={fileTypePopoverOpen === file.id}
                                  onOpenChange={(open) =>
                                    setFileTypePopoverOpen(open ? file.id : null)
                                  }
                                >
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      aria-expanded={fileTypePopoverOpen === file.id}
                                      disabled={importing}
                                      className={cn(
                                        "h-8 w-full justify-between text-xs",
                                        !hasType && "border-orange-300 bg-orange-50"
                                      )}
                                    >
                                      {fileTypes[file.id]
                                        ? FILE_TYPE_OPTIONS.find(
                                            (opt) => opt.value === fileTypes[file.id]
                                          )?.label
                                        : "Select type..."}
                                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[200px] p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
                                    <Command>
                                      <CommandInput placeholder="Search type..." />
                                      <CommandList>
                                        <CommandEmpty>No type found.</CommandEmpty>
                                        <CommandGroup>
                                          {FILE_TYPE_OPTIONS.map((option) => (
                                            <CommandItem
                                              key={option.value}
                                              value={option.label}
                                              onSelect={() => {
                                                handleFileTypeChange(file.id, option.value);
                                                setFileTypePopoverOpen(null);
                                              }}
                                              className="text-xs"
                                            >
                                              <Check
                                                className={cn(
                                                  "mr-2 h-4 w-4",
                                                  fileTypes[file.id] === option.value
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                                )}
                                              />
                                              {option.label}
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              ) : !isFolder && !isSelected ? (
                                <span className="text-xs text-gray-400">-</span>
                              ) : null}
                            </div>
                          )}

                          {/* Size (Desktop) */}
                          <div className="flex flex-col items-end gap-0.5 min-w-0">
                            {!isFolder && !isSizeValid && (
                              <span className="text-red-500 text-[10px] flex items-center gap-0.5">
                                <AlertCircle className="h-3 w-3" />
                                &gt;25MB
                              </span>
                            )}
                            {!isFolder && !isTypeValid && isSizeValid && (
                              <span className="text-orange-500 text-[10px] flex items-center gap-0.5">
                                <AlertCircle className="h-3 w-3" />
                                Invalid
                              </span>
                            )}
                            <span
                              className={`text-xs text-right truncate ${!isValid && !isFolder
                                ? "text-red-500"
                                : isGDoc
                                  ? "text-blue-600 font-medium"
                                  : "text-gray-500"
                                }`}
                            >
                              {formatSize(file.size, file.mimeType)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Selected files from other folders */}
                  {(() => {
                    const otherFolderFiles = Array.from(selectedFiles)
                      .filter((id) => !files.some((f) => f.id === id))
                      .map((id) => selectedFilesData[id])
                      .filter(Boolean);
                    if (otherFolderFiles.length === 0) return null;
                    return (
                      <>
                        <div className="bg-gray-50 px-3 py-2 border-t border-b flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500">
                            Selected from other folders ({otherFolderFiles.length})
                          </span>
                        </div>
                        {otherFolderFiles.map((file) => {
                          const isGDoc = isGoogleDoc(file.mimeType);
                          const hasType = !!fileTypes[file.id];
                          return (
                            <div
                              key={file.id}
                              data-state="selected"
                              onClick={() => !importing && handleToggleFile(file)}
                              className="block sm:grid sm:grid-cols-[auto_1fr_80px] items-center gap-3 p-3 transition-colors cursor-pointer bg-blue-50 hover:bg-blue-100"
                              style={mode !== "select" ? { gridTemplateColumns: "auto 1fr 200px 80px" } : undefined}
                            >
                              {/* Mobile Layout */}
                              <div className="sm:hidden overflow-hidden">
                                <div className="flex items-center gap-3 overflow-hidden">
                                  <Checkbox
                                    checked={true}
                                    onCheckedChange={() => handleToggleFile(file)}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={importing}
                                    className="flex-shrink-0"
                                  />
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {getFileIcon(file.mimeType)}
                                    <p
                                      className="w-0 flex-grow truncate text-sm font-medium text-blue-900"
                                      title={file.name}
                                    >
                                      {file.name}
                                    </p>
                                    <ViewInDriveButton file={file} />
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-1.5 ml-9">
                                  <span className={`text-xs ${isGDoc ? "text-blue-600 font-medium" : "text-gray-500"}`}>
                                    {formatSize(file.size, file.mimeType)}
                                  </span>
                                </div>
                                {mode !== "select" && (
                                  <div className="mt-1.5 ml-9" onClick={(e) => e.stopPropagation()}>
                                    <Popover
                                      modal={false}
                                      open={fileTypePopoverOpen === `mobile-${file.id}`}
                                      onOpenChange={(open) => setFileTypePopoverOpen(open ? `mobile-${file.id}` : null)}
                                    >
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="outline"
                                          role="combobox"
                                          aria-expanded={fileTypePopoverOpen === `mobile-${file.id}`}
                                          disabled={importing}
                                          className={cn(
                                            "h-7 w-full justify-between text-xs",
                                            !hasType && "border-orange-300 bg-orange-50"
                                          )}
                                        >
                                          {fileTypes[file.id]
                                            ? FILE_TYPE_OPTIONS.find((opt) => opt.value === fileTypes[file.id])?.label
                                            : "Select type..."}
                                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-[200px] p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
                                        <Command>
                                          <CommandInput placeholder="Search type..." />
                                          <CommandList>
                                            <CommandEmpty>No type found.</CommandEmpty>
                                            <CommandGroup>
                                              {FILE_TYPE_OPTIONS.map((option) => (
                                                <CommandItem
                                                  key={option.value}
                                                  value={option.label}
                                                  onSelect={() => {
                                                    handleFileTypeChange(file.id, option.value);
                                                    setFileTypePopoverOpen(null);
                                                  }}
                                                  className="text-xs"
                                                >
                                                  <Check
                                                    className={cn("mr-2 h-4 w-4", fileTypes[file.id] === option.value ? "opacity-100" : "opacity-0")}
                                                  />
                                                  {option.label}
                                                </CommandItem>
                                              ))}
                                            </CommandGroup>
                                          </CommandList>
                                        </Command>
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                )}
                              </div>

                              {/* Desktop Layout */}
                              <div className="hidden sm:contents">
                                <Checkbox
                                  checked={true}
                                  onCheckedChange={() => handleToggleFile(file)}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={importing}
                                />
                                <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                                  {getFileIcon(file.mimeType)}
                                  <span className="truncate text-sm font-medium text-blue-900" title={file.name}>
                                    {file.name}
                                  </span>
                                  <ViewInDriveButton file={file} />
                                </div>
                                {mode !== "select" && (
                                  <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
                                    <Popover
                                      modal={false}
                                      open={fileTypePopoverOpen === file.id}
                                      onOpenChange={(open) => setFileTypePopoverOpen(open ? file.id : null)}
                                    >
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="outline"
                                          role="combobox"
                                          disabled={importing}
                                          className={cn(
                                            "h-8 w-full justify-between text-xs",
                                            !hasType && "border-orange-300 bg-orange-50"
                                          )}
                                        >
                                          {fileTypes[file.id]
                                            ? FILE_TYPE_OPTIONS.find((opt) => opt.value === fileTypes[file.id])?.label
                                            : "Select type..."}
                                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-[200px] p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
                                        <Command>
                                          <CommandInput placeholder="Search type..." />
                                          <CommandList>
                                            <CommandEmpty>No type found.</CommandEmpty>
                                            <CommandGroup>
                                              {FILE_TYPE_OPTIONS.map((option) => (
                                                <CommandItem
                                                  key={option.value}
                                                  value={option.label}
                                                  onSelect={() => {
                                                    handleFileTypeChange(file.id, option.value);
                                                    setFileTypePopoverOpen(null);
                                                  }}
                                                  className="text-xs"
                                                >
                                                  <Check
                                                    className={cn("mr-2 h-4 w-4", fileTypes[file.id] === option.value ? "opacity-100" : "opacity-0")}
                                                  />
                                                  {option.label}
                                                </CommandItem>
                                              ))}
                                            </CommandGroup>
                                          </CommandList>
                                        </Command>
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                )}
                                <div className="flex flex-col items-end gap-0.5 min-w-0">
                                  <span className={`text-xs text-right truncate ${isGDoc ? "text-blue-600 font-medium" : "text-gray-500"}`}>
                                    {formatSize(file.size, file.mimeType)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </ScrollArea>
            </div>

            {/* Footer */}
            <DialogFooter className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t pt-4">
              <div className="flex flex-col gap-1">
                <div className="text-sm text-gray-600 flex items-center gap-2 flex-wrap">
                  <span className="font-medium">
                    {selectedFiles.size} file
                    {selectedFiles.size !== 1 ? "s" : ""} selected
                  </span>
                  {selectedTotalSize > 0 && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span>{formatSize(selectedTotalSize)}</span>
                    </>
                  )}
                  {selectedGoogleDocsCount > 0 && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span>
                        {selectedGoogleDocsCount} Google{" "}
                        {selectedGoogleDocsCount === 1 ? "Doc" : "Docs"}
                      </span>
                    </>
                  )}
                </div>
                {mode !== "select" &&
                  selectedFiles.size > 0 &&
                  filesWithoutTypes > 0 && (
                    <p className="text-xs text-orange-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {filesWithoutTypes} file
                      {filesWithoutTypes !== 1 ? "s" : ""} need
                      {filesWithoutTypes === 1 ? "s" : ""} document type
                    </p>
                  )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={importing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!canImport}
                  className="min-w-[120px]"
                >
                  {mode === "select" ? (
                    <>
                      Select Files{" "}
                      {selectedFiles.size > 0 ? `(${selectedFiles.size})` : ""}
                    </>
                  ) : importing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      Import{" "}
                      {selectedFiles.size > 0 ? `(${selectedFiles.size})` : ""}
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
